import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup
} from '@mui/material';
import {
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { activityLogsApi } from '../services/api';
import { UserRoleContext } from './Layout';

interface ActivityLog {
  id: string;
  logId: string;
  username: string;
  userRole: string;
  action: string;
  resource: string;
  resourceName?: string;
  resourceId?: string;
  details?: string;
  ipAddress: string;
  status: string;
  sessionId?: string;
  requestId?: string;
  createdAt: string;
  formattedDate: string;
}

interface ActivityStats {
  actionStats: Array<{ _id: string; count: number }>;
  userStats: Array<{ _id: string; count: number; role: string }>;
  resourceStats: Array<{ _id: string; count: number }>;
  statusStats: Array<{ _id: string; count: number }>;
  dailyActivity: Array<{ _id: string; count: number }>;
  activeUsers?: {
    count: number;
    recentUsers: Array<{
      username: string;
      userRole: string;
      lastActivity: string;
      lastActivityFormatted: string;
    }>;
  };
}

const ActivityLogs: React.FC = () => {
  const { role } = useContext(UserRoleContext);
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [stats, setStats] = useState<ActivityStats | null>(null);
  const [activeUsers, setActiveUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    logId: '',
    username: '',
    userRole: '',
    action: '',
    resource: '',
    status: '',
    sessionId: '',
    requestId: '',
    resourceId: ''
  });

  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('general');

  // Bulk Delete
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [totalLogsCount, setTotalLogsCount] = useState(0);

  // Fetch logs
  const fetchLogs = async () => {
    if (role !== 'master_admin') return;
    setLoading(true);
    setError(null);
    
    try {
      const data = await activityLogsApi.getLogs({
        page: page + 1,
        limit: rowsPerPage,
        ...filters,
      });
      setLogs(data.logs);
      setTotal(data.pagination.total);
    } catch (err: any) {
      // Ako je token istekao, axios interceptor će pokušati refresh;
      // ovde samo prikaži poruku iz backend-a ako postoji.
      const message =
        err?.response?.data?.error ||
        (err instanceof Error ? err.message : 'Greška pri dohvatanju podataka');
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Search logs
  const searchLogs = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        query: searchQuery,
        type: searchType
      });

      const response = await fetch(`/api/activity-logs/search?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Greška pri pretraživanju');
      }

      const data = await response.json();
      setLogs(data.logs);
      setTotal(data.total);
      setPage(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Greška pri pretraživanju');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    if (role !== 'master_admin') return;
    try {
      const data = await activityLogsApi.getStats();
      setStats(data);
    } catch (err) {
      // Statistika nije kritična; grešku možemo ignorisati ili eventualno prikazati kasnije
    }
  };

  // Export logs
  const exportLogs = async () => {
    try {
      const params = new URLSearchParams({
        format: 'csv'
      });

      // Add filters to export
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const response = await fetch(`/api/activity-logs/export?${params}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activity-logs-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (err) {
      setError('Greška pri izvozu logova');
    }
  };

  // Fetch active users
  const fetchActiveUsers = async () => {
    if (role !== 'master_admin') return;
    try {
      const data = await activityLogsApi.getActiveUsers();
      setActiveUsers(data.activeUsers || []);
    } catch (err) {
      // console.error('Greška pri dohvatanju aktivnih korisnika:', err);
    }
  };

  // Effects
  useEffect(() => {
    if (role === 'master_admin') {
      fetchLogs();
      fetchStats();
      fetchActiveUsers();
    }
  }, [page, rowsPerPage, role]);

  // Auto-refresh active users every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchActiveUsers();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Handlers
  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field: string, value: any) => {
    // console.log('🔍 Filter change:', field, value);
    setFilters(prev => {
      const newFilters = { ...prev, [field]: value };
      // console.log('🔍 New filters:', newFilters);
      return newFilters;
    });
    setPage(0);
    // Automatski osveži podatke kada se filteri promene
    setTimeout(() => fetchLogs(), 100);
  };

  const clearFilters = () => {
    setFilters({
      logId: '',
      username: '',
      userRole: '',
      action: '',
      resource: '',
      status: '',
      sessionId: '',
      requestId: '',
      resourceId: ''
    });
    setPage(0);
    // Automatski osveži podatke kada se filteri očiste
    setTimeout(() => fetchLogs(), 100);
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      searchLogs();
    } else {
      fetchLogs();
    }
  };

  // Helper functions
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'error': return 'error';
      case 'warning': return 'warning';
      default: return 'default';
    }
  };

  const getActionLabel = (action: string) => {
    const labels: { [key: string]: string } = {
      login: 'Ulogovanje',
      logout: 'Odjavljivanje',
      create: 'Kreiranje',
      update: 'Izmena',
      delete: 'Brisanje',
      verify: 'Verifikacija',
      reject: 'Odbijanje',
      view: 'Pregled',
      export: 'Izvoz',
      import: 'Uvoz',
      template_edit: 'Izmena šablona',
      user_management: 'Upravljanje korisnicima',
      settings_change: 'Promena podešavanja'
    };
    return labels[action] || action;
  };

  const getResourceLabel = (resource: string) => {
    const labels: { [key: string]: string } = {
      measurement: 'Merenje',
      product: 'Proizvod',
      user: 'Korisnik',
      template: 'Šablon',
      lambda_group: 'Lambda grupa',
      table_settings: 'Podešavanja tabele',
      system: 'Sistem',
      auth: 'Autentifikacija'
    };
    return labels[resource] || resource;
  };

  // Bulk Delete Functions
  const handleBulkDelete = async () => {
    if (!bulkDeleteConfirm) return;

    setBulkDeleteLoading(true);
    try {
      const result = await activityLogsApi.bulkDeleteAll();
      
      // Refresh logs after deletion
      await fetchLogs();
      await fetchStats();
      
      setBulkDeleteDialog(false);
      setBulkDeleteConfirm(false);
      
      // Show success message
      setError(null);
    } catch (error) {
      // console.error('Greška pri bulk brisanju:', error);
      setError('Greška pri bulk brisanju logova');
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const openBulkDeleteDialog = () => {
    setBulkDeleteDialog(true);
    setBulkDeleteConfirm(false);
    setTotalLogsCount(total);
  };

  // Load data on mount
  useEffect(() => {
    if (role === 'master_admin') {
      fetchLogs();
      fetchStats();
      fetchActiveUsers();
    }
  }, [role]);

  if (role !== 'master_admin') {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Samo master admin može da pristupi logovima aktivnosti.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      <Typography variant="h5" gutterBottom>
        📊 Logovi Aktivnosti
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

              {/* Statistics Cards */}
        {stats && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Ukupno Akcija
                  </Typography>
                  <Typography variant="h5">
                    {stats.actionStats.reduce((sum, stat) => sum + stat.count, 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography color="textSecondary" gutterBottom>
                      Aktivni Korisnici
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Auto-osvežavanje
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={fetchActiveUsers}
                        startIcon={<RefreshIcon />}
                        sx={{ minWidth: 'auto', p: 0.5 }}
                      >
                        Osveži
                      </Button>
                    </Box>
                  </Box>
                  
                  {activeUsers.length > 0 ? (
                    <Box>
                      <Typography variant="h5" sx={{ mb: 1 }}>
                        {activeUsers.filter(u => u.isActive).length} aktivnih
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {activeUsers.slice(0, 3).map((user, index) => (
                          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {user.isActive && (
                              <Box sx={{ 
                                width: 6, 
                                height: 6, 
                                borderRadius: '50%', 
                                backgroundColor: 'success.main',
                                animation: 'pulse 2s infinite'
                              }} />
                            )}
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {user.username}
                            </Typography>
                            <Chip 
                              label={user.userRole === 'master_admin' ? 'MA' : 
                                     user.userRole === 'admin' ? 'A' : 'K'}
                              size="small"
                              color={user.userRole === 'master_admin' ? 'error' : 
                                     user.userRole === 'admin' ? 'warning' : 'primary'}
                              sx={{ fontSize: '0.7rem', height: 20 }}
                            />
                          </Box>
                        ))}
                        {activeUsers.length > 3 && (
                          <Typography variant="caption" color="textSecondary">
                            +{activeUsers.length - 3} više...
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ) : (
                    <Typography variant="h5" color="text.secondary">
                      0 aktivnih
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Uspešnih Akcija
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {stats.statusStats.find(s => s._id === 'success')?.count || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    Grešaka
                  </Typography>
                  <Typography variant="h5" color="error.main">
                    {stats.statusStats.find(s => s._id === 'error')?.count || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>
        )}



              {/* Search Bar */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            <SearchIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Pretraživanje
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
              <TextField
                fullWidth
                label="Pretraži..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Unesite ID, korisnika, resurs..."
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <FormControl fullWidth>
                <InputLabel>Tip pretrage</InputLabel>
                <Select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  label="Tip pretrage"
                >
                  <MenuItem value="general">Opšta pretraga</MenuItem>
                  <MenuItem value="logId">Log ID</MenuItem>
                  <MenuItem value="sessionId">Session ID</MenuItem>
                  <MenuItem value="requestId">Request ID</MenuItem>
                  <MenuItem value="resourceId">Resource ID</MenuItem>
                  <MenuItem value="username">Korisnik</MenuItem>
                  <MenuItem value="ip">IP Adresa</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={handleSearch}
                startIcon={<SearchIcon />}
              >
                Pretraži
              </Button>
              <Button
                variant="outlined"
                onClick={fetchLogs}
                startIcon={<RefreshIcon />}
              >
                Osveži
              </Button>
            </Box>
          </Box>
        </Paper>

              {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            <FilterIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Filteri
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
              <TextField
                fullWidth
                label="Log ID"
                value={filters.logId}
                onChange={(e) => handleFilterChange('logId', e.target.value)}
                size="small"
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
              <TextField
                fullWidth
                label="Korisnik"
                value={filters.username}
                onChange={(e) => handleFilterChange('username', e.target.value)}
                size="small"
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={filters.userRole}
                  onChange={(e) => handleFilterChange('userRole', e.target.value)}
                  label="Role"
                >
                  <MenuItem value="">Svi</MenuItem>
                  <MenuItem value="master_admin">Master Admin</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="tehnolog">Kontrolor</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="">Svi</MenuItem>
                  <MenuItem value="success">Uspešno</MenuItem>
                  <MenuItem value="error">Greška</MenuItem>
                  <MenuItem value="warning">Upozorenje</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
              <TextField
                fullWidth
                label="Session ID"
                value={filters.sessionId}
                onChange={(e) => handleFilterChange('sessionId', e.target.value)}
                size="small"
              />
            </Box>
            <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
              <TextField
                fullWidth
                label="Request ID"
                value={filters.requestId}
                onChange={(e) => handleFilterChange('requestId', e.target.value)}
                size="small"
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%', mt: 2 }}>
              <Button
                variant="contained"
                onClick={fetchLogs}
                startIcon={<FilterIcon />}
              >
                Primeni Filtere
              </Button>
              <Button
                variant="outlined"
                onClick={clearFilters}
              >
                Očisti Filtere
              </Button>
              <Button
                variant="contained"
                color="secondary"
                onClick={exportLogs}
                startIcon={<DownloadIcon />}
              >
                Izvezi CSV
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={openBulkDeleteDialog}
                startIcon={<DeleteIcon />}
              >
                Obriši Sve Logove
              </Button>
            </Box>
          </Box>
        </Paper>

      {/* Logs Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Log ID</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Datum/Vreme</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Korisnik</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Role</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Akcija</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Resurs</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Detalji</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>IP</TableCell>
                <TableCell sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    Nema logova za prikaz
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell sx={{ fontSize: '0.7rem' }}>
                      <Tooltip title="Kopiraj ID">
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace', 
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            '&:hover': { backgroundColor: '#f5f5f5' }
                          }}
                          onClick={() => navigator.clipboard.writeText(log.logId)}
                        >
                          {log.logId.substring(0, 12)}...
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>{log.formattedDate}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>{log.username}</TableCell>
                    <TableCell>
                      <Chip 
                        label={log.userRole} 
                        size="small" 
                        color="primary" 
                        variant="outlined"
                        sx={{ fontSize: '0.65rem' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>{getActionLabel(log.action)}</TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>
                      {log.resourceName || getResourceLabel(log.resource)}
                      {log.resourceId && (
                        <Typography variant="caption" display="block" color="textSecondary" sx={{ fontSize: '0.6rem' }}>
                          ID: {log.resourceId}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>
                      {log.details && (
                        <Tooltip title={log.details}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontSize: '0.7rem' }}>
                            {log.details}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.7rem' }}>{log.ipAddress}</TableCell>
                    <TableCell>
                      <Chip 
                        label={log.status} 
                        size="small" 
                        color={getStatusColor(log.status) as any}
                        sx={{ fontSize: '0.65rem' }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          labelRowsPerPage="Redova po stranici:"
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} od ${count !== -1 ? count : `više od ${to}`}`
          }
        />
      </Paper>

      {/* Bulk Delete Dialog */}
      <Dialog 
        open={bulkDeleteDialog} 
        onClose={() => setBulkDeleteDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="error" />
            Potvrdite Bulk Brisanje
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 3 }}>
            Ova akcija će obrisati sve logove. 
            <strong>Ova akcija je nepovratna!</strong>
          </DialogContentText>

          <Box sx={{ mb: 3, p: 2, bgcolor: 'white', borderRadius: 1}}>
            <Typography variant="subtitle2" color="error" gutterBottom>
              Upozorenje:
            </Typography>
            <Typography variant="body2" color="error">
              <strong>{totalLogsCount} logova</strong> će biti obrisano
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={bulkDeleteConfirm}
                  onChange={(e) => setBulkDeleteConfirm(e.target.checked)}
                  color="error"
                />
              }
              label="Potvrđujem da želim da obrišem sve logove"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setBulkDeleteDialog(false)}
            disabled={bulkDeleteLoading}
          >
            Otkaži
          </Button>
          <Button 
            onClick={handleBulkDelete}
            disabled={!bulkDeleteConfirm || bulkDeleteLoading}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            {bulkDeleteLoading ? <CircularProgress size={20} /> : 'Obriši Sve'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityLogs; 